import { formatHours } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Plus, CheckCircle2, Edit, Trash2, StickyNote } from "lucide-react";

const WEEKDAY_LONG = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];

function DayBlock({ d, items, isToday, onOpenNew, onComplete, onEdit, onDelete }) {
  const [y, m, day] = d.date.split("-");
  const weekday = WEEKDAY_LONG[d.weekday] || "";
  const disabled = !d.is_workday || d.is_holiday;

  return (
    <div
      className={`relative flex flex-col rounded-lg overflow-hidden border transition-shadow ${
        isToday ? "border-blue-500 shadow-lg shadow-blue-500/10" : d.is_holiday ? "border-red-200" : "border-stone-300"
      } bg-[#faf7ee]`}
      data-testid={`weekview-day-${d.date}`}
      style={{
        backgroundImage:
          "repeating-linear-gradient(transparent, transparent 27px, rgba(15, 23, 42, 0.08) 28px)",
        backgroundPosition: "0 60px",
      }}
    >
      {/* Header with weekday + big day number */}
      <div className="relative px-4 pt-3 pb-2 border-b border-stone-200 bg-[#faf7ee]/95">
        <div className="flex items-baseline justify-between gap-2">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wider text-blue-800 font-semibold truncate">
              {weekday} {isToday && <span className="ml-1 text-blue-600">· Hoy</span>}
            </div>
            <div className="font-display text-5xl font-bold leading-none text-slate-800 mt-0.5">{day}</div>
          </div>
          <div className="text-right shrink-0">
            <div className="text-[11px] uppercase text-stone-500">{m}/{y}</div>
            {d.is_holiday && (
              <div className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 mt-1">
                Festivo{d.holiday_label ? ` · ${d.holiday_label}` : ""}
              </div>
            )}
            {!d.is_holiday && !d.is_workday && (
              <div className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-500 mt-1">No laborable</div>
            )}
          </div>
        </div>
      </div>

      {/* Appointments area (looks like ruled lines) */}
      <div className="flex-1 min-h-[220px] px-4 py-3 space-y-2">
        {disabled && (
          <div className="text-sm italic text-stone-400 pt-4">
            {d.is_holiday ? "Día festivo, sin citas." : "Día no laborable."}
          </div>
        )}
        {!disabled && items.length === 0 && (
          <div className="text-sm italic text-stone-400 pt-4">Sin citas programadas.</div>
        )}
        {items.map((it, i) => (
          <div
            key={`${it.repair_id}-${i}`}
            className={`group flex items-start gap-2 py-1 border-b border-stone-200/60 ${
              it.status === "completed" ? "opacity-60 line-through" : ""
            }`}
            data-testid={`weekview-item-${it.repair_id}`}
          >
            <span className="text-blue-700 font-bold tabular-nums w-10 shrink-0 text-sm mt-0.5">
              {formatHours(it.hours)}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800 truncate" title={it.description}>
                {it.description}
              </div>
              {(it.license_plate || it.customer_name) && (
                <div className="text-xs text-stone-500 truncate">
                  {it.license_plate && <span className="font-mono">{it.license_plate}</span>}
                  {it.license_plate && it.customer_name && " · "}
                  {it.customer_name}
                  {it.notes && (
                    <span className="ml-1 inline-flex items-center text-amber-600" title={it.notes}>
                      <StickyNote className="w-3 h-3" />
                    </span>
                  )}
                </div>
              )}
            </div>
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1 shrink-0">
              <button
                onClick={() => onEdit(it.repair_id)}
                className="p-1 text-slate-500 hover:text-blue-700"
                title="Editar"
                data-testid={`weekview-edit-${it.repair_id}`}
              >
                <Edit className="w-3.5 h-3.5" />
              </button>
              {it.status !== "completed" && (
                <button
                  onClick={() => onComplete(it.repair_id)}
                  className="p-1 text-slate-500 hover:text-emerald-600"
                  title="Completar"
                  data-testid={`weekview-complete-${it.repair_id}`}
                >
                  <CheckCircle2 className="w-3.5 h-3.5" />
                </button>
              )}
              <button
                onClick={() => onDelete(it.repair_id)}
                className="p-1 text-slate-500 hover:text-red-600"
                title="Eliminar"
                data-testid={`weekview-delete-${it.repair_id}`}
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Footer: capacity bar + add button */}
      <div className="px-4 py-2 border-t border-stone-200 bg-white/50">
        {!disabled ? (
          <>
            <div className="flex items-center justify-between text-[11px] text-stone-600 mb-1">
              <span>
                <span className="font-semibold text-slate-800">{formatHours(d.used)}</span>
                {" / "}
                {formatHours(d.schedulable)}
              </span>
              <span className="text-stone-400">de {formatHours(d.total_mechanic_hours)}</span>
            </div>
            <div className="h-1.5 bg-stone-200 rounded-full overflow-hidden mb-2">
              <div
                className={`h-full ${d.used >= d.schedulable ? "bg-red-400" : "bg-blue-500"}`}
                style={{ width: `${Math.min(100, (d.used / Math.max(d.schedulable, 0.001)) * 100)}%` }}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenNew(d)}
              className="w-full h-8 text-blue-700 hover:bg-blue-50 hover:text-blue-800"
              data-testid={`weekview-add-${d.date}`}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Añadir cita
            </Button>
          </>
        ) : (
          <div className="h-8" />
        )}
      </div>
    </div>
  );
}

const GRID_CLASS_BY_COLS = {
  1: "grid-cols-1",
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
};

export default function WeekView({ days, agenda, onOpenNew, onComplete, onEdit, onDelete, cols = 1 }) {
  const gridClass = GRID_CLASS_BY_COLS[cols] || GRID_CLASS_BY_COLS[1];
  return (
    <div
      className="rounded-2xl bg-gradient-to-b from-stone-100 to-stone-50 p-3 sm:p-6 border border-stone-200 shadow-inner"
      data-testid="week-view"
    >
      <div className={`relative grid ${gridClass} gap-3 sm:gap-4`}>
        {days.map((d, i) => {
          const items = agenda[d.date] || [];
          const isToday = d.date === new Date().toISOString().slice(0, 10);
          return (
            <DayBlock
              key={d.date}
              d={d}
              items={items}
              isToday={isToday}
              onOpenNew={onOpenNew}
              onComplete={onComplete}
              onEdit={onEdit}
              onDelete={onDelete}
            />
          );
        })}
      </div>
      <p className="text-xs text-stone-500 mt-4 text-center italic">
        Pasa el ratón sobre una cita para ver las acciones · Los días completos aparecen con la barra en rojo
      </p>
    </div>
  );
}
